import React, { useEffect, useRef, useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Button,
  InputNumber,
  Row,
  Col,
  Tag,
  Spin,
  message
} from 'antd';
import DateTimeInput from './DateTimeInput';
import {
  ExpandOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import TextArea from 'antd/es/input/TextArea';
import LocationPicker from "../components/LocationPicker";
import dayjs from 'dayjs';
import { useGeoMapModal, DEFAULT_MAP_POSITION } from "../utils/geoFormUtils";
import { validateLatitude, validateLongitude } from "../utils/validators";
import { makaniService } from "../services/makaniService";

const defaultPosition = DEFAULT_MAP_POSITION;

export default function ComplaintModal({
  open,
  mode = 'create',
  initialValues,
  status = 0,
  creating = false,
  editing = false,
  resolving = false,
  onCancel,
  onCreate,
  onUpdate,
  onResolve,
  onReopen,
  readOnly = false
}) {
  const [form] = Form.useForm();
  const safeInitialValues = initialValues || {};
  const [requireObsOnResolve, setRequireObsOnResolve] = useState(false);
  const [makaniLoading, setMakaniLoading] = useState(false);
  const makaniDebounceRef = useRef(null);

  const {
    markerPosition,
    showMap,
    expanded,
    mapRefreshKey,
    expandedMapKey,
    setSmallMap,
    setExpandedMap,
    updateMarkerPosition,
    openExpandedMap,
    closeExpandedMap,
  } = useGeoMapModal({
    open,
    lat: safeInitialValues.latitude,
    lng: safeInitialValues.longitude,
    defaultPosition,
  });

  const disabledFields = readOnly || (mode === 'edit' && status === 1);

  const triggerMakaniLookup = (lat, lng) => {
    if (makaniDebounceRef.current) clearTimeout(makaniDebounceRef.current);
    makaniDebounceRef.current = setTimeout(async () => {
      setMakaniLoading(true);
      try {
        const result = await makaniService.lookup(lat, lng);
        form.setFieldValue('makaniNumber', result);
      } catch {
        form.setFieldValue('makaniNumber', '');
        message.error('Could not retrieve Makani number for this location');
      } finally {
        setMakaniLoading(false);
      }
    }, 600);
  };

  const handleFieldChange = (changed, all) => {
    if ('latitude' in changed || 'longitude' in changed) {
      const lat = Number(all.latitude);
      const lng = Number(all.longitude);
      if (!isNaN(lat) && !isNaN(lng)) {
        updateMarkerPosition([lat, lng], form);
        if (!disabledFields) triggerMakaniLookup(lat, lng);
      }
    }
  };

  const handleOk = (values) => {
    const payload = {
      community: values.community,
      latitude: values.latitude,
      longitude: values.longitude,
      date: `${values.date.format('YYYY-MM-DD')} ${values.time.format('HH:mm')}`,
      description: values.description,
      observations: values.observations,
      makaniNumber: values.makaniNumber
    };

    if (mode === 'create') {
      onCreate(payload, form);
    } else if (mode === 'edit') {
      onUpdate(payload, form);
    }
  };

  const handleFinishFailed = ({ errorFields }) => {
    if (errorFields?.length) {
      form.scrollToField(errorFields[0].name, {
        behavior: 'smooth',
        block: 'center'
      });
    }
  };

  const handleMapChange = (pos) => {
    const lat = Number(pos[0]);
    const lng = Number(pos[1]);

    if (isNaN(lat) || isNaN(lng)) return;

    updateMarkerPosition([lat, lng], form);

    if (!disabledFields) {
      triggerMakaniLookup(lat, lng);
    }
  };

  useEffect(() => {
    if (open) {
      setRequireObsOnResolve(false);

      form.setFieldsValue({
        community: safeInitialValues.community || '',
        latitude: safeInitialValues.latitude ?? '',
        longitude: safeInitialValues.longitude ?? '',
        date: safeInitialValues.date ? dayjs(safeInitialValues.date) : null,
        time: safeInitialValues.date ? dayjs(safeInitialValues.date) : null,
        description: safeInitialValues.description || '',
        observations: safeInitialValues.observations || '',
        makaniNumber: safeInitialValues.makaniNumber || ''
      });

      if (
        typeof safeInitialValues.latitude === 'number' &&
        typeof safeInitialValues.longitude === 'number'
      ) {
        updateMarkerPosition(
          [safeInitialValues.latitude, safeInitialValues.longitude],
          form
        );
        if (!disabledFields && !safeInitialValues.makaniNumber) {
          triggerMakaniLookup(safeInitialValues.latitude, safeInitialValues.longitude);
        }
      } else {
        updateMarkerPosition(defaultPosition, form);
      }
    }
  }, [open, initialValues]);

  return (
    <>
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontWeight: 600 }}>
              {mode === 'create'
                ? 'Create Complaint'
                : `Complaint ${safeInitialValues.id}`}
            </span>

            {mode === 'edit' && (
              status === 1 ? (
                <Tag icon={<CheckCircleOutlined />} color="success">
                  Resolved
                </Tag>
              ) : (
                <Tag icon={<ClockCircleOutlined />} color="warning">
                  Pending
                </Tag>
              )
            )}
          </div>
        }
        open={open}
        maskClosable={false}
        width={760}
        style={{ top: 50 }}
        styles={{
          body: {
            maxHeight: '70vh',
            overflowY: 'auto',
            paddingRight: 8
          }
        }}
        onCancel={() => {
          setRequireObsOnResolve(false);
          onCancel && onCancel();
          form.resetFields();
        }}
        footer={[
          <Button key="cancel" onClick={() => { onCancel && onCancel(); form.resetFields(); }}>
            Cancel
          </Button>,

          !readOnly && mode === 'edit' && status === 1 && (
            <Button
              key="reopen"
              type="primary"
              onClick={() => onReopen && onReopen(safeInitialValues.id, form)}
            >
              Reopen
            </Button>
          ),

          !readOnly && mode === 'edit' && status === 0 && (
            <Button key="save" type="primary" loading={editing} onClick={() => form.submit()}>
              Save
            </Button>
          ),

          !readOnly && mode === 'edit' && status === 0 && (
            <Button
              key="resolve"
              type="default"
              danger
              loading={resolving}
              onClick={async () => {
                setRequireObsOnResolve(true);
                try {
                  await form.validateFields(['observations']);
                  onResolve && onResolve(safeInitialValues.id, form);
                } catch {
                  form.scrollToField('observations', {
                    behavior: 'smooth',
                    block: 'center'
                  });
                }
              }}
            >
              Resolve
            </Button>
          ),

          !readOnly && mode === 'create' && (
            <Button key="create" type="primary" loading={creating} onClick={() => form.submit()}>
              Create
            </Button>
          )
        ]}
        destroyOnHidden
      >
        <Form
          layout="vertical"
          form={form}
          onValuesChange={handleFieldChange}
          onFinish={handleOk}
          onFinishFailed={handleFinishFailed}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Community" name="community" rules={[{ required: true }]}>
                <Input disabled={disabledFields} />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item label="Date">
                <DateTimeInput dateName="date" timeName="time" disabled={disabledFields} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Latitude" name="latitude" rules={[{ required: true }, { validator: validateLatitude }]}>
                <InputNumber style={{ width: '100%' }} step={0.000001} disabled={disabledFields} />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item label="Longitude" name="longitude" rules={[{ required: true }, { validator: validateLongitude }]}>
                <InputNumber style={{ width: '100%' }} step={0.000001} disabled={disabledFields} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Makani Number"
            name="makaniNumber"
            rules={[{ required: true, message: 'Makani number is required. Select a location on the map.' }]}
          >
            <Input
              disabled
              placeholder="Auto-populated from location"
              suffix={makaniLoading ? <Spin size="small" /> : null}
            />
          </Form.Item>

          <Form.Item label="Select location on map">
            <div style={{ position: 'relative', height: 220 }}>
              <Button
                size="small"
                icon={<ExpandOutlined />}
                style={{ position: 'absolute', top: 12, right: 12, zIndex: 1000 }}
                onClick={openExpandedMap}
              >
                Expand map
              </Button>

              {showMap && (
                <LocationPicker
                  value={markerPosition}
                  onChange={handleMapChange}
                  disabled={disabledFields}
                  setContainerMap={setSmallMap}
                  forceRefresh={mapRefreshKey}
                />
              )}
            </div>
          </Form.Item>

          <Form.Item label="Description" name="description" rules={[{ required: true }]}>
            <TextArea rows={3} maxLength={500} showCount disabled={disabledFields} />
          </Form.Item>

          <Form.Item
            label="Observations"
            name="observations"
            rules={[
              {
                validator: (_, value) => {
                  if (requireObsOnResolve && !value) {
                    return Promise.reject(
                      new Error('Observations are required to resolve')
                    );
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <TextArea rows={3} maxLength={500} showCount disabled={disabledFields} />
          </Form.Item>

          {mode === 'edit' && status === 1 && (
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item label="Resolved by">
                  <Input value={safeInitialValues.resolvedBy || '-'} disabled />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item label="Resolved at">
                  <Input value={safeInitialValues.resolvedAt || '-'} disabled />
                </Form.Item>
              </Col>
            </Row>
          )}
        </Form>
      </Modal>

      <Modal
        open={expanded}
        onCancel={closeExpandedMap}
        footer={null}
        width={900}
        centered
        styles={{ body: { padding: 0 } }}
        destroyOnHidden
      >
        <div style={{ height: 500, position: 'relative' }}>
          <Button
            type="primary"
            size="large"
            style={{ position: 'absolute', top: 18, right: 28, zIndex: 1200 }}
            onClick={closeExpandedMap}
          >
            Select
          </Button>

          <LocationPicker
            value={markerPosition}
            onChange={handleMapChange}
            disabled={disabledFields}
            setContainerMap={setExpandedMap}
            forceRefresh={expandedMapKey}
          />
        </div>
      </Modal>
    </>
  );
}
